// Blocking step: billing province + postal code must exist before any checkout (drives Stripe Tax)
import { useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/services/api';
import { PROVINCES, FEDERAL_TAX, formatPostalCode, isValidPostalCode } from '@/constants/provinces';

export default function BillingAddressGate({ landlord, onSaved }) {
  const [province, setProvince] = useState(landlord?.province || '');
  const [postalCode, setPostalCode] = useState(landlord?.postal_code || '');
  const [saving, setSaving] = useState(false);
  const tax = FEDERAL_TAX[province];

  const save = async () => {
    if (!province) { toast.error('Select your province or territory'); return; }
    if (!isValidPostalCode(postalCode)) { toast.error('Enter a valid Canadian postal code (e.g. M5V 3L9)'); return; }
    setSaving(true);
    try {
      await api.put('/auth/profile', { province, postal_code: postalCode });
      toast.success('Billing address saved');
      onSaved({ province, postal_code: postalCode });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save billing address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-amber-300 rounded-lg p-4" data-testid="billing-address-gate">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-800">Confirm your billing province</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">Sales tax on subscriptions is charged based on where you live. This is required once before you can subscribe or make a payment.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
            <div className="sm:col-span-2">
              <Select value={province} onValueChange={setProvince}>
                <SelectTrigger className="h-9 text-sm bg-white" data-testid="billing-province-select"><SelectValue placeholder="Province / Territory" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {PROVINCES.map(([code, name]) => <SelectItem key={code} value={code} className="text-sm" data-testid={`billing-province-${code}`}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={postalCode} onChange={(e) => setPostalCode(formatPostalCode(e.target.value))} placeholder="Postal code" className="h-9 text-sm font-mono" data-testid="billing-postal-code-input" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Button onClick={save} disabled={saving} className="h-9 text-sm bg-teal-600 hover:bg-teal-700 text-white" data-testid="billing-address-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save and continue'}
            </Button>
            {tax && <p className="text-xs text-slate-500" data-testid="billing-tax-preview">{tax.rate}% {tax.label} will be added at checkout</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
